import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { useReportTemplates, type ReportTemplateSummary } from '@/hooks/reports/useReportTemplates';
import { useCreateReport } from '@/hooks/reports/useReports';
import { useHasPermission } from '@/hooks/usePermissions';

type OwnerType = 'JOB' | 'LEAD' | 'CUSTOMER';
const OWNER_TYPES: ReadonlyArray<OwnerType> = ['JOB', 'LEAD', 'CUSTOMER'];

export const Route = createFileRoute('/_dashboard/dashboard/reports/new')({
  validateSearch: (search): { ownerType?: OwnerType; ownerId?: string } => ({
    ownerType: OWNER_TYPES.includes(search.ownerType as OwnerType)
      ? (search.ownerType as OwnerType)
      : undefined,
    ownerId: typeof search.ownerId === 'string' ? search.ownerId : undefined,
  }),
  component: NewReportPage,
});

function NewReportPage() {
  const navigate = useNavigate();
  const { ownerType: prefilledOwnerType, ownerId: prefilledOwnerId } = Route.useSearch();
  const { data: templates } = useReportTemplates();
  const create = useCreateReport();
  const canCreateReports = useHasPermission('reports:create');

  const [templateId, setTemplateId] = useState<string>('');
  const [ownerType, setOwnerType] = useState<OwnerType>(prefilledOwnerType ?? 'JOB');
  const [ownerId, setOwnerId] = useState(prefilledOwnerId ?? '');
  const [title, setTitle] = useState('');

  const ownerLocked = Boolean(prefilledOwnerType && prefilledOwnerId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = (await create.mutateAsync({
      templateId,
      ownerType,
      ownerId,
      title: title || undefined,
    })) as { id: string };
    navigate({ to: '/dashboard/reports/$id', params: { id: result.id } });
  };

  const list = ((templates as ReportTemplateSummary[]) ?? []).filter(
    (t) => t.status === 'PUBLISHED',
  );

  if (!canCreateReports) {
    return (
      <div className="p-6 max-w-2xl mx-auto text-center">
        <p className="text-sm text-muted-foreground">
          You do not have permission to create reports.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">New Report</h1>
      <p className="text-muted-foreground text-sm mb-6">
        Pick a template and the job/lead/customer this report is for.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Template">
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a template…" />
            </SelectTrigger>
            <SelectContent>
              {list.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {ownerLocked ? (
          <Field label="For">
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
              {ownerType.charAt(0) + ownerType.slice(1).toLowerCase()}
              <span className="ml-2 font-mono text-xs text-muted-foreground">{ownerId}</span>
            </div>
          </Field>
        ) : (
          <>
            <Field label="Owner type">
              <Select value={ownerType} onValueChange={(v) => setOwnerType(v as typeof ownerType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="JOB">Job</SelectItem>
                  <SelectItem value="LEAD">Lead</SelectItem>
                  <SelectItem value="CUSTOMER">Customer</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={`${ownerType.toLowerCase()} id`}>
              <Input
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                placeholder="UUID"
                required
              />
            </Field>
          </>
        )}
        <Field label="Title (optional)">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Defaults to template name + date"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="submit" disabled={!templateId || !ownerId || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-sm font-medium mb-1 block">{label}</label>
      {children}
    </div>
  );
}
